// Inspection failures: failed dock inspections, tracked in the app until QA closes them.
//
// Amit's ruling, 22 Oct 2026: no Planner task; the failure lives here (migration 020, rule 24).
// A failure is an inspection lineage in which any version failed - a passing correction never
// erases it - and it stays OPEN until a RELEASE holder closes it with the outcome for the load
// (accepted, rejected, accepted with conditions - migration 022) and a note. The person who
// recorded the failing inspection cannot close it; the server refuses them whatever this shows.
//
// Rule 15: a list that could not be read says so and offers to ask again; it is never "no
// failures".
//
// `?failure=<id>` (a Teams goods-in card's Close out, rule 26) opens the page on that one failure,
// whatever its status; the person closes it here, under their own sign-in.
import { useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { BATCH_CHECKS, VEHICLE_CHECKS } from '../operator/receiveModel';
import { DataTable } from '@/components/DataTable';
import { Saved } from '@/components/Saved';
import { api } from '../../lib/api';
import { currentUpn } from '../../config/auth';
import { fieldClass, labelClass, officeFocus, serverMessage, useMyGrants, useResource } from '../../shells/OfficeShell';
import { PhotoThumb } from './GoodsInReceipt';
import { NcrPanel, useNcrConfig, useReturnFocus, type NcrPrefill } from './RaiseNcr';
import { PutOnHold, useHoldConfig } from './BatchHolds';
import {
  CLOSURE_OUTCOME_LABEL,
  TONE_CLASS,
  chipClass,
  closesOwnFailure,
  outcomeLabel,
  type ClosureOutcome,
  dayLabel,
  photoCaption,
  stamp,
  visibleText,
  type InspectionFailure,
  type InspectionFailuresPayload,
} from './goodsInModel';

const col = createColumnHelper<InspectionFailure>();

const STATUS_LABEL = { OPEN: 'Open', CLOSED: 'Closed' } as const;

/** The close-out form. `onClosed` is told what was recorded, in words, for the page's Saved line:
 *  the row may leave the open list on reload, so the line lives above the table, not in the row. */
function CloseForm({ f, onOpen, onClosed }: { f: InspectionFailure; onOpen: () => void; onClosed: (said: string) => void }) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState<ClosureOutcome | ''>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // UX-46 (UI/UX audit, 31 Oct 2026): "Close failure" was greyed out until both fields were filled,
  // with no word on why - a disabled button explains nothing and a screen reader skips it. It stays
  // enabled; a submit with a gap says which field, beside it, and sends nothing. The gate is the
  // same (nothing is posted without an outcome and a note with a letter or digit in it), and the
  // server still refuses an empty note whatever this screen does.
  const [tried, setTried] = useState(false);
  const ids = useId();
  const outcomeMissing = outcome === '';
  const noteMissing = !visibleText(note);
  const trigger = useReturnFocus<HTMLButtonElement>(open);
  if (!open) {
    return (
      <Button
        ref={trigger}
        variant="outline"
        type="button"
        onClick={() => {
          onOpen();
          setOpen(true);
        }}
      >
        Close out
      </Button>
    );
  }
  async function submit() {
    setTried(true);
    if (outcomeMissing || noteMissing) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/inspection-failures/${f.failureId}/close`, { outcome, note });
      setOpen(false);
      onClosed(`Inspection failure #${f.failureId} closed: ${CLOSURE_OUTCOME_LABEL[outcome]} - ${note.trim()}`);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex min-w-[16rem] flex-col gap-(--s1)">
      <label className="flex flex-col gap-(--s1)">
        <span className={labelClass}>Outcome for the load (required)</span>
        {/* Takes focus when the form opens (finding 19): it mounts only on Close out. */}
        <select
          autoFocus
          aria-required="true"
          aria-invalid={tried && outcomeMissing ? true : undefined}
          aria-describedby={tried && outcomeMissing ? `${ids}-outcome` : undefined}
          className={`${fieldClass} ${officeFocus}`}
          value={outcome}
          onChange={(e) => setOutcome(e.target.value as ClosureOutcome | '')}
        >
          <option value="">Choose, after the risk assessment</option>
          {(Object.keys(CLOSURE_OUTCOME_LABEL) as ClosureOutcome[]).map((o) => (
            <option key={o} value={o}>
              {CLOSURE_OUTCOME_LABEL[o]}
            </option>
          ))}
        </select>
      </label>
      {tried && outcomeMissing ? (
        <p id={`${ids}-outcome`} className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          Choose the outcome for the load.
        </p>
      ) : null}
      <label className="flex flex-col gap-(--s1)">
        <span className={labelClass}>{outcome === 'ACCEPTED_WITH_CONDITIONS' ? 'The conditions, and what was done (required)' : 'What was done (required)'}</span>
        <textarea
          aria-required="true"
          aria-invalid={tried && noteMissing ? true : undefined}
          aria-describedby={tried && noteMissing ? `${ids}-note` : undefined}
          className={`${fieldClass} ${officeFocus}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      {tried && noteMissing ? (
        <p id={`${ids}-note`} className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          Write what was done{outcome === 'ACCEPTED_WITH_CONDITIONS' ? ', and the conditions' : ''}.
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          {error}
        </p>
      ) : null}
      {tried && (outcomeMissing || noteMissing) ? (
        <p role="status" className="sr-only">
          Not closed: {[outcomeMissing ? 'choose the outcome' : null, noteMissing ? 'write what was done' : null].filter(Boolean).join(' and ')}.
        </p>
      ) : null}
      <div className="flex gap-(--s2)">
        <Button type="button" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Closing…' : 'Close failure'}
        </Button>
        <Button variant="outline" type="button" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** What an NCR raised from a failure starts with: its supplier, its lines and the failed checks. */
export function failurePrefill(f: InspectionFailure): NcrPrefill {
  return {
    vendor: f.supplier?.account ?? null,
    lines: f.lines.filter((l) => l.item).map((l) => ({ item: l.item as string, label: `${l.po}${l.line !== 1 ? ` / ${l.line}` : ''} · ${l.itemName}`, batches: l.batches ?? [] })),
    description: `Incoming inspection failure #${f.failureId}: ${f.failedChecks.map((x) => `${x.where}: ${x.label}${x.reason ? ` - ${x.reason}` : ''}`).join('; ')}`,
  };
}

/**
 * The words for a photo's check (UI/UX audit, 31 Oct 2026, UX-38). The caption printed the raw
 * code ("NO_PESTS") - or "delivery" for none - where the receipt page says "Vehicle: No evidence of
 * pest infestation". The failure's own labels first (the server's words for what failed), then the
 * dock form's, so a photo of a check that passed is named too; a code neither knows is shown as it
 * is (photoCaption's fallback), never dropped.
 */
const FORM_CHECK_LABELS: ReadonlyArray<{ check: string; label: string }> = [...VEHICLE_CHECKS, ...BATCH_CHECKS].map((c) => ({ check: c.key, label: c.label }));
export function failureCheckLabels(f: Pick<InspectionFailure, 'failedChecks'>): ReadonlyArray<{ check: string; label: string }> {
  return [...f.failedChecks.map((x) => ({ check: x.check, label: x.label })), ...FORM_CHECK_LABELS];
}

/** The failed checks with their reasons: the one list the row and the sheet both show. */
function FailedChecks({ f }: { f: InspectionFailure }) {
  return (
    <ul className="m-0 list-none p-0">
      {f.failedChecks.map((x, i) => (
        <li key={`${x.inspectionId}-${x.where}-${x.check}-${i}`}>
          <span className="font-semibold text-(--bad)">
            {x.where}: {x.label}
          </span>{' '}
          - {x.reason ?? 'no reason recorded'}
        </li>
      ))}
      {f.corrected ? <li className="text-(--muted)">Corrected since: the newest record passes.</li> : null}
    </ul>
  );
}

/** Open or closed, and the closure's who, when, outcome and note. */
function FailureStatus({ f }: { f: InspectionFailure }) {
  return (
    <div className="flex flex-col gap-(--s1)">
      <span className={`${chipClass} w-fit ${TONE_CLASS[f.status === 'OPEN' ? 'bad' : 'ok']}`}>
        {STATUS_LABEL[f.status]}
        {f.reopened ? ' again' : ''}
      </span>
      {f.closure ? (
        <span className="text-(length:--fs-xs) text-(--muted)">
          {f.status === 'OPEN' ? 'Was closed' : 'Closed'} by {f.closure.closedByUpn} {stamp(f.closure.closedAt)}.{' '}
          <span className="font-semibold">{outcomeLabel(f.closure.outcome)}</span>: {f.closure.note}
        </span>
      ) : null}
    </div>
  );
}

const sectionHeading = 'm-0 font-display text-[18px] font-semibold leading-tight';

/**
 * One failure, opened (UI/UX audit, 31 Oct 2026, UX-37). Close out, Raise NCR and QC Hold used to
 * sit inside the Status cell of an eight-column table, beside 112 px photo thumbnails, so each form
 * opened in a column a few words wide. A failure is read before it is acted on, so the sheet goes in
 * that order: what failed and why, the photos, where it stands, and only then the actions. The
 * trigger sits inside the Sheet so focus returns to it on close (the CertificateFile pattern).
 * Every control keeps its own gate: Close out only for a RELEASE holder who recorded no version
 * (rule 9's sentence stands where Close out would be), and the server refuses whatever this shows.
 */
function FailureSheet({
  f,
  open,
  onOpenChange,
  canClose,
  me,
  grants,
  ncrConfig,
  holdConfig,
  onCloseOpen,
  onClosed,
}: {
  f: InspectionFailure;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canClose: boolean;
  me: string;
  grants: ReturnType<typeof useMyGrants>;
  ncrConfig: ReturnType<typeof useNcrConfig>;
  holdConfig: ReturnType<typeof useHoldConfig>;
  onCloseOpen: () => void;
  onClosed: (said: string) => void;
}) {
  const labels = failureCheckLabels(f);
  const own = closesOwnFailure(f, me);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button variant="outline" type="button" aria-label={`Open inspection failure #${f.failureId}`}>
          Open
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col gap-(--s4) overflow-y-auto sm:max-w-[min(720px,92vw)]">
        <SheetHeader>
          <SheetTitle>Inspection failure #{f.failureId}</SheetTitle>
          <SheetDescription>
            {f.supplier?.name ?? f.supplier?.account ?? 'Supplier unknown'} · received {dayLabel(f.dateReceived)} · inspected by {f.inspectedByUpn ?? 'unknown'} ·{' '}
            {f.ageDays} d old
          </SheetDescription>
        </SheetHeader>
        <section className="flex flex-col gap-(--s2) text-(length:--fs-sm)" aria-label="Failed checks">
          <h3 className={sectionHeading}>Failed checks</h3>
          <FailedChecks f={f} />
        </section>
        {f.photos.length > 0 ? (
          <section className="flex flex-col gap-(--s2)" aria-label="Photos">
            <h3 className={sectionHeading}>Photos</h3>
            {/* PhotoThumb renders its own <li> (UX-38): it goes straight into the list, never
                inside a second <li>. */}
            <ul className="m-0 flex list-none flex-wrap gap-(--s2) p-0">
              {f.photos.map((p) => (
                <PhotoThumb key={`${p.inspectionId}-${p.photoId}`} inspectionId={p.inspectionId} photo={p} caption={photoCaption(p, labels)} />
              ))}
            </ul>
          </section>
        ) : null}
        <section className="flex flex-col gap-(--s2) text-(length:--fs-sm)" aria-label="Status">
          <h3 className={sectionHeading}>Status</h3>
          <FailureStatus f={f} />
        </section>
        <section className="flex flex-col gap-(--s2) text-(length:--fs-sm)" aria-label="Actions">
          <h3 className={sectionHeading}>Actions</h3>
          {f.status === 'OPEN' && canClose && !own ? <CloseForm f={f} onOpen={onCloseOpen} onClosed={onClosed} /> : null}
          {f.status === 'OPEN' && canClose && own ? (
            <p className="m-0 text-(length:--fs-sm) text-(--muted)">You inspected or corrected it, so another QA person closes it.</p>
          ) : null}
          <NcrPanel config={ncrConfig} source={{ kind: 'INSPECTION_FAILURE', id: f.failureId }} prefill={failurePrefill(f)} grants={grants} />
          {f.status === 'OPEN' ? <PutOnHold config={holdConfig} source={{ kind: 'INSPECTION_FAILURE', id: f.failureId }} prefill={failurePrefill(f)} grants={grants} /> : null}
        </section>
      </SheetContent>
    </Sheet>
  );
}

/** The failure a link names (`?failure=900`), or null when it names none or nonsense. */
export function focusedFailure(param: string | null): number | null {
  return param && /^[1-9]\d{0,9}$/.test(param) ? Number(param) : null;
}

export function InspectionFailures() {
  const [params] = useSearchParams();
  const focus = focusedFailure(params.get('failure'));
  const [chosen, setStatus] = useState<'open' | 'closed' | 'all'>('open');
  // One failure, open or closed: the card's link must find it whatever has happened since.
  const status = focus !== null ? 'all' : chosen;
  const list = useResource<InspectionFailuresPayload>(`/inspection-failures?status=${status}`);
  const rows = focus !== null ? (list.data?.failures ?? []).filter((f) => f.failureId === focus) : (list.data?.failures ?? []);
  const grants = useMyGrants();
  const me = currentUpn();
  const canClose = grants.known && grants.holds('RELEASE');
  const ncrConfig = useNcrConfig();
  const holdConfig = useHoldConfig();
  // The last close-out recorded (audit finding 15), above the table: the row it was on may have
  // left the open list. Cleared when the next close-out form opens.
  const [saved, setSaved] = useState<string | null>(null);
  // The failure opened in its sheet (UX-37). A Teams card's link (`?failure=`) is there to close it
  // out, so that one opens on arrival rather than one click further on.
  const [openId, setOpenId] = useState<number | null>(focus);

  const columns = [
    col.accessor((f) => f.supplier?.name ?? f.supplier?.account ?? '', {
      id: 'supplier',
      header: 'Supplier',
      cell: (c) => c.getValue() || 'unknown',
      meta: { rowHeader: true },
    }),
    col.accessor((f) => f.lines.map((l) => `${l.po}/${l.line}`).join(' '), {
      id: 'po',
      header: 'PO / line',
      // A failure on a vehicle inspection (migration 033) has no batch rows: its PO lines are the
      // truck's. None sent is not known, never "no lines" (rule 15).
      cell: (c) =>
        c.row.original.lines.length === 0 ? (
          <span className="text-(--muted)">PO lines not known</span>
        ) : (
          <ul className="m-0 list-none p-0">
            {c.row.original.lines.map((l) => (
              <li key={l.receiptId}>
                <Link to={`/office/goods-in/${l.receiptId}`} className={`underline ${officeFocus}`}>
                  {l.po}
                  {l.line !== 1 ? ` / ${l.line}` : ''}
                </Link>{' '}
                {l.itemName}
              </li>
            ))}
          </ul>
        ),
    }),
    col.accessor((f) => f.dateReceived ?? '', { id: 'date', header: 'Received', cell: (c) => dayLabel(c.row.original.dateReceived), meta: { nowrap: true } }),
    col.accessor((f) => f.failedChecks.map((x) => x.label).join(' '), {
      id: 'checks',
      header: 'Failed checks',
      cell: (c) => <FailedChecks f={c.row.original} />,
    }),
    // UX-37 (UI/UX audit, 31 Oct 2026): a count here, the pictures in the sheet. 112 px thumbnails
    // in a cell were most of the row's width and pushed the actions into a sliver beside them.
    col.accessor((f) => f.photos.length, {
      id: 'photos',
      header: 'Photos',
      cell: (c) => (c.getValue() === 0 ? '—' : `${c.getValue()} photo${c.getValue() === 1 ? '' : 's'}`),
      meta: { align: 'right', nowrap: true },
    }),
    col.accessor((f) => f.inspectedByUpn ?? '', { id: 'inspector', header: 'Inspector', cell: (c) => c.getValue() || 'unknown' }),
    col.accessor((f) => f.ageDays, { id: 'age', header: 'Age', cell: (c) => `${c.getValue()} d`, meta: { align: 'right', nowrap: true } }),
    col.accessor((f) => f.status, {
      id: 'status',
      header: 'Status',
      cell: (c) => <FailureStatus f={c.row.original} />,
    }),
    // The way in to the failure's photos and its actions (UX-37). Named per row (UX-45's shape).
    col.display({
      id: 'open',
      header: () => <span className="sr-only">Details and actions</span>,
      cell: (c) => {
        const f = c.row.original;
        return (
          <FailureSheet
            f={f}
            open={openId === f.failureId}
            onOpenChange={(o) => setOpenId(o ? f.failureId : null)}
            canClose={canClose}
            me={me}
            grants={grants}
            ncrConfig={ncrConfig}
            holdConfig={holdConfig}
            onCloseOpen={() => setSaved(null)}
            onClosed={(said) => {
              // The Saved line is above the table, so the sheet closes to show it; the row may
              // leave the open list on reload.
              setSaved(said);
              setOpenId(null);
              list.reload();
            }}
          />
        );
      },
    }),
  ];

  return (
    <div className="flex flex-col gap-(--s5)">
      <header>
        <h1 className="m-0 font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">Inspection failures</h1>
        <p className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">
          Deliveries that failed a dock check. Each stays open until QA closes it with the outcome and a note.
        </p>
      </header>
      {focus !== null ? (
        <p className="m-0 text-(length:--fs-sm)">
          Showing inspection failure #{focus}.{' '}
          <Link to="/office/inspection-failures" className={`underline ${officeFocus}`}>
            Show all open failures
          </Link>
        </p>
      ) : (
        <label className="flex w-fit flex-col gap-(--s1)">
          <span className={labelClass}>Show</span>
          <select className={`${fieldClass} ${officeFocus}`} value={chosen} onChange={(e) => setStatus(e.target.value as typeof chosen)}>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
        </label>
      )}
      {saved ? <Saved>{saved}</Saved> : null}
      {list.error ? (
        <div role="alert" className="flex flex-wrap items-center gap-(--s2)">
          <p className="m-0 font-semibold text-(--warn)">
            Could not read the inspection failures: {list.error} This is not the same as "none".
          </p>
          <Button variant="outline" type="button" onClick={list.reload}>
            Try again
          </Button>
        </div>
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          caption="Failed dock inspections, newest first"
          getRowId={(f) => String(f.failureId)}
          rowAccent={(f) => (f.status === 'OPEN' ? 'bad' : null)}
          loading={list.loading}
          search={(list.data?.failures.length ?? 0) > 10 ? 'Search supplier, PO or check' : undefined}
          empty={
            list.data
              ? focus !== null
                ? `No inspection failure #${focus} in the list. Check the link, or show all failures.`
                : status === 'open'
                  ? 'No open inspection failures.'
                  : 'No inspection failures.'
              : null
          }
        />
      )}
    </div>
  );
}

export default InspectionFailures;
